import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

export const StatCard = ({ icon: Icon, label, value, accent = '#00B4D8', testid }) => (
  <div className="kite-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground"><Icon className="w-4 h-4" style={{ color: accent }} /><span className="label-eyebrow">{label}</span></div>
    <div className="font-data text-3xl font-semibold mt-2" style={{ color: accent }}>{value}</div>
  </div>
);

export const PlanBadge = ({ tier }) => {
  if (tier === 'elite') return <Badge className="bg-[#FFB020] text-black">ELITE</Badge>;
  const pro = tier === 'pro' || tier === 'pro_camp';
  return <Badge className={pro ? 'bg-[#FF6B35] text-black' : 'bg-white/10 text-white'}>{pro ? 'PRO' : 'FREE'}</Badge>;
};

const ENGAGEMENT_STYLES = {
  active: 'bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30',
  at_risk: 'bg-[#FFB020]/15 text-[#FFB020] border-[#FFB020]/30',
  dormant: 'bg-[#FF6B35]/15 text-[#FF6B35] border-[#FF6B35]/30',
  new: 'bg-[#48CAE4]/15 text-[#48CAE4] border-[#48CAE4]/30',
};

export const EngagementBadge = ({ status, t }) => (
  <Badge variant="outline" className={`${ENGAGEMENT_STYLES[status] || ENGAGEMENT_STYLES.new} text-[11px]`} data-testid="engagement-badge">
    {t(`admin.engagement.${status || 'new'}`)}
  </Badge>
);

export const AnalysisStatusBadge = ({ status, t }) => {
  const map = {
    completed: 'border-[#2EE59D]/40 text-[#2EE59D]',
    processing: 'border-[#48CAE4]/40 text-[#48CAE4]',
    failed: 'border-[#FF6B35]/40 text-[#FF6B35]',
  };
  return (
    <Badge variant="outline" className={`${map[status] || 'border-white/20 text-muted-foreground'} text-[11px]`}>
      {status === 'completed' ? <CheckCircle2 className="w-3 h-3 mr-1" /> : status === 'failed' ? <AlertTriangle className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
      {t(`admin.analysisStatus.${status}`, { defaultValue: status })}
    </Badge>
  );
};

/** Locale-aware date/time formatters for the admin pages. */
export function useAdminFmt() {
  const { i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2);
  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : '\u2014');
  const fmtDateTime = (s) => (s ? new Date(s).toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '\u2014');
  return { locale, fmtDate, fmtDateTime };
}
